// The reharm engine's front door (plan §6): a REGISTRY of moves in the mould
// of voiceLeadingEngines.ts — a new move is a new row, never new plumbing —
// and `suggest()`, which runs every move at a chord, gates on the melody,
// grades, and ranks. Applying a candidate is formEdit.replaceChord with the
// move as provenance; the store wraps that in a layer (plan §5).

import * as formEdit from '../score/formEdit';
import { activeReharmLayer, createReharmLayer, dropReharmPreview } from '../score/reharm';
import type { Score } from '../score/types';
import type { ReharmCandidate } from './types';

export { buildReharmContext, melodyIn, sequenceIndexOf } from './context';
export { REHARM_MOVES } from './moves';
export { melodyFitFor, unresolvedAlteredFor } from './melodyFit';
export { scoreCandidate, sequenceWith } from './score';
export { suggest, suggestForChord, moveById, type SuggestOptions } from './suggest';
export {
  derive,
  applyDerivation,
  derivationBand,
  resolveDeriveOptions,
  type DeriveOptions,
} from './derive';
export {
  ADVENTURE_BANDS,
  ADVENTURE_CEILING,
  adventureBandOf,
  meanSurprisal,
  transitionBits,
  type AdventureBand,
} from './surprisal';
export type * from './types';

/**
 * Write a candidate into the score — into the active layer, or into a fresh
 * layer created for it when none is active, so the original is never
 * overwritten by a suggestion. An audition is never that layer: it is taken
 * off first, since anything filed into it goes when it does. One Score in,
 * one Score out: the store commits it as a single undo step. Null when the
 * chord is gone (or only ever existed in the audition) or the span refuses.
 */
export function applyCandidate(score: Score, candidate: ReharmCandidate): Score | null {
  const settled = dropReharmPreview(score);
  const layered = activeReharmLayer(settled) ? settled : createReharmLayer(settled).score;
  return formEdit.replaceChord(layered, candidate.chordId, candidate.chords, {
    moveId: candidate.moveId,
    rationale: candidate.rationale,
  });
}
